import React from 'react'
import clsx from 'clsx'
import {
  faListUl,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import {
  DetailRow,
  DetailInfo,
} from '../../../../common/components/DetailRow/DetailRow'
import { BoxV4, StatusChipV4 } from '@labrav/react-components'
import { CONTRACT_DETAILS_COLLAPSIBLE_STYLE_V2 } from '../SubscriptionDetail.styles'
import { renderSubscriptionDetailHeadingIcon } from './renderSubscriptionDetailHeadingIcon'
import { getStatusAndLabelForSubscription } from '../SubscriptionStatus/SubscriptionStatus'
import { DateTime } from 'luxon'
import { standardLabraDateFormat } from '@labrav/common'
import { useFlag } from '@labrav/flags'
import { productDetailDate } from '../../../../common/utils/productDetailDate'
import {
  getContractDuration,
  getSubscriptionContractStartDate,
} from '../subscriptionDetailHelpers'
import { SubscriptionDetailObject } from '../../../../modules/subscription/reducer'
import { ViewValueWithTruncate } from '@labrav/common'
import { TextWithCopyIcon } from '@labrav/common'
import {
  compactDisplayId,
  getGcpContractStatusLabel,
  getGcpStatusChipVariant,
  openHttpsUrl,
} from '../utils/subscriptionDetailHelperImports'

interface ContractDetailsSectionProps {
  subscriptionDetail: SubscriptionDetailObject
  offerDetailInfo: {
    companyName?: string
    netPaymentTerms?: string
  }
  timeZone: string
  classes: any
  cloudType?: string
}

const renderDateWithTime = (
  dateStr: string,
  timeZone: string,
  classes: any
) => {
  const { modifiedDate, modifiedTime } = productDetailDate(dateStr, timeZone)
  return (
    <div className={classes.dateTimeContainer}>
      <div className={classes.createdAtDate}>{modifiedDate}</div>
      {modifiedTime && (
        <div className={classes.createdAtTime}>{modifiedTime.trim()}</div>
      )}
    </div>
  )
}

export const ContractDetailsSection: React.FC<ContractDetailsSectionProps> = ({
  subscriptionDetail,
  offerDetailInfo,
  timeZone,
  classes,
  cloudType,
}) => {
  const { flag: contractStatusUpdate } = useFlag('contractStatusUpdate')
  const isGcp = cloudType === 'GCP'
  const contractStartDate = getSubscriptionContractStartDate(subscriptionDetail)
  const isFdaSpecifiedStartDate =
    Boolean(subscriptionDetail?.offerDetails?.agreementStartDate) &&
    subscriptionDetail?.offerDetails?.variableStartDate !== true

  const netPaymentTerms =
    subscriptionDetail?.offerDetails?.netPaymentTerms != null
      ? `${subscriptionDetail?.offerDetails?.netPaymentTerms} days`
      : '-'
  const isAws = cloudType === 'AWS'
  const awsCurrencyCode =
    subscriptionDetail?.subscription?.currency ||
    subscriptionDetail?.entitlements?.[0]?.currency ||
    'USD'
  const awsCurrencySymbol =
    subscriptionDetail?.subscription?.currencySymbol ||
    subscriptionDetail?.entitlements?.[0]?.currencySymbol ||
    '$'

  if (isGcp && subscriptionDetail) {
    const contractDetails = subscriptionDetail.contractDetails
    if (!contractDetails) return null
    const chipVariant = getGcpStatusChipVariant(contractDetails.contractStatus)
    const contractDurationLabel =
      contractDetails.contractDuration != null
        ? `${contractDetails.contractDuration} months`
        : '-'

    return (
      <div className={classes.contractDetailsBoxWrapper}>
        <BoxV4
          variant="small"
          headingVariant="small"
          heading="Contract details"
          headingIcon={renderSubscriptionDetailHeadingIcon(classes)}
          collapsible
          collapsibleStyle={CONTRACT_DETAILS_COLLAPSIBLE_STYLE_V2}
        >
          <div
            className={classes.contractDetailsBodyInner}
            data-testid="contract-details-collapsible-body"
          >
            <div
              className={clsx(
                classes.sectionContentV2,
                classes.contractSectionContent
              )}
            >
              <DetailRow borderBottom props={{ spacing: 3 }}>
                <>
                  <DetailInfo
                    title="Buyer"
                    value={contractDetails.buyerName || '-'}
                    keyName="buyer"
                  />
                  <DetailInfo
                    title="Contract status"
                    value={
                      contractDetails.contractStatus ? (
                        <div className={classes.contractStatusCell}>
                          {contractDetails.daysLeft != null &&
                            contractDetails.daysLeft > 0 && (
                              <span className={classes.daysLeftText}>
                                {contractDetails.daysLeft} days left
                              </span>
                            )}
                          <StatusChipV4
                            label={getGcpContractStatusLabel(
                              contractDetails.contractStatus
                            )}
                            variant={chipVariant}
                            size="small"
                          />
                        </div>
                      ) : (
                        '-'
                      )
                    }
                    keyName="contractStatus"
                  />
                </>
              </DetailRow>
              <DetailRow borderBottom props={{ spacing: 1 }}>
                <>
                  <DetailInfo
                    title="TCV"
                    value={
                      contractDetails.tcv != null ? (
                        <span>
                          <span className={classes.currencySymbol}>$</span>
                          {contractDetails.tcv.toLocaleString()}
                        </span>
                      ) : (
                        '-'
                      )
                    }
                    keyName="tcv"
                  />
                  <DetailInfo
                    title="Contract duration"
                    value={contractDurationLabel}
                    keyName="contractDuration"
                  />
                </>
              </DetailRow>
              <DetailRow borderBottom props={{ spacing: 1 }}>
                <>
                  <DetailInfo
                    title="Contract ID"
                    value={
                      contractDetails.contractId ? (
                        <TextWithCopyIcon
                          text={contractDetails.contractId}
                          displayText={compactDisplayId(
                            contractDetails.contractId
                          )}
                        />
                      ) : (
                        '-'
                      )
                    }
                    keyName="contractId"
                  />
                  <DetailInfo
                    title="Auto renew"
                    value={
                      contractDetails.autoRenew != null
                        ? contractDetails.autoRenew
                          ? 'Yes'
                          : 'No'
                        : '-'
                    }
                    keyName="autoRenew"
                  />
                </>
              </DetailRow>
              <DetailRow borderBottom props={{ spacing: 1 }}>
                <>
                  <DetailInfo
                    title="Renewal type"
                    value={contractDetails.renewalType || '-'}
                    keyName="renewalType"
                  />
                  <DetailInfo
                    title="No. of renewals"
                    value={
                      contractDetails.numberOfRenewals != null
                        ? String(contractDetails.numberOfRenewals)
                        : '-'
                    }
                    keyName="noOfRenewals"
                  />
                </>
              </DetailRow>
              <DetailRow borderBottom props={{ spacing: 1 }}>
                <>
                  <DetailInfo
                    title="Contract start date"
                    value={
                      contractDetails.contractStartDate
                        ? renderDateWithTime(
                            contractDetails.contractStartDate,
                            timeZone,
                            classes
                          )
                        : '-'
                    }
                    keyName="contractStartDate"
                  />
                  <DetailInfo
                    title="Contract end date"
                    value={
                      contractDetails.contractEndDate
                        ? renderDateWithTime(
                            contractDetails.contractEndDate,
                            timeZone,
                            classes
                          )
                        : '-'
                    }
                    keyName="contractEndDate"
                  />
                </>
              </DetailRow>
              <DetailRow borderBottom={false} props={{ spacing: 1 }}>
                <>
                  <DetailInfo
                    title="Currency"
                    value={contractDetails.currency || '-'}
                    keyName="currency"
                  />
                  <DetailInfo
                    title="Product"
                    value={
                      <div className={classes.productValue}>
                        <span>{contractDetails.product || '-'}</span>
                        {contractDetails.productUrl && (
                          <FontAwesomeIcon
                            icon={faArrowUpRightFromSquare}
                            className={classes.productLinkIcon}
                            onClick={() =>
                              openHttpsUrl(contractDetails.productUrl as string)
                            }
                            title="View in Marketplace"
                          />
                        )}
                      </div>
                    }
                    keyName="gcpProduct"
                  />
                </>
              </DetailRow>
            </div>
          </div>
        </BoxV4>
      </div>
    )
  }

  return (
    <SectionWrapper heading="Contract details" icon={faListUl}>
      <div className={classes.sectionContent}>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Buyer"
              value={offerDetailInfo?.companyName || '-'}
              keyName="buyer"
            />
            <DetailInfo
              title="Contract status"
              value={
                subscriptionDetail?.subscription
                  ? (() => {
                      const statusLabel = getStatusAndLabelForSubscription(
                        subscriptionDetail.subscription.status,
                        subscriptionDetail.subscription.expirationDate,
                        DateTime.now,
                        {
                          treatPastExpiryAsExpired:
                            !!contractStatusUpdate?.value,
                        }
                      )

                      return (
                        <StatusChipV4
                          label={statusLabel.label}
                          variant={
                            statusLabel.label === 'Active'
                              ? 'success'
                              : statusLabel.label === 'Expired'
                                ? 'error'
                                : 'warning'
                          }
                          size="small"
                        />
                      )
                    })()
                  : '-'
              }
              keyName="contractStatus"
            />
          </>
        </DetailRow>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="TCV"
              value={
                subscriptionDetail?.subscription?.tcv !== undefined &&
                subscriptionDetail?.subscription?.tcv !== null ? (
                  <span>
                    <span className={classes.currencySymbol}>
                      {isAws ? awsCurrencySymbol : '$'}
                    </span>
                    {subscriptionDetail.subscription.tcv.toLocaleString()}
                  </span>
                ) : (
                  '-'
                )
              }
              keyName="tcv"
            />
            <DetailInfo
              title="Contract duration"
              value={getContractDuration(
                subscriptionDetail?.subscription,
                contractStartDate
              )}
              keyName="contractDuration"
            />
          </>
        </DetailRow>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Contract ID"
              value={
                <ViewValueWithTruncate
                  value={
                    subscriptionDetail.subscription.awsAgreementId ||
                    subscriptionDetail.subscription.azureSubscriptionId
                  }
                  showIcon
                />
              }
              keyName="contractId"
            />
            <DetailInfo
              title="Auto renew"
              value={subscriptionDetail.subscription.autoRenew ? 'Yes' : 'No'}
              keyName="autoRenew"
            />
          </>
        </DetailRow>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Contract start date"
              value={
                contractStartDate
                  ? isFdaSpecifiedStartDate
                    ? DateTime.fromISO(contractStartDate, {
                        zone: timeZone,
                      }).toFormat(standardLabraDateFormat)
                    : renderDateWithTime(contractStartDate, timeZone, classes)
                  : '-'
              }
              keyName="contractStartDate"
            />
            <DetailInfo
              title="Contract end date"
              value={
                subscriptionDetail.subscription.expirationDate
                  ? isFdaSpecifiedStartDate
                    ? DateTime.fromISO(
                        subscriptionDetail.subscription.expirationDate,
                        { zone: timeZone }
                      ).toFormat(standardLabraDateFormat)
                    : renderDateWithTime(
                        subscriptionDetail.subscription.expirationDate,
                        timeZone,
                        classes
                      )
                  : '-'
              }
              keyName="contractEndDate"
            />
          </>
        </DetailRow>
        <DetailRow borderBottom={false} props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Currency"
              value={
                isAws
                  ? `${awsCurrencyCode}(${awsCurrencySymbol})`
                  : subscriptionDetail?.entitlements[0]?.currency || 'USD'
              }
              keyName="Currency"
            />
            <DetailInfo
              title="Net payment terms"
              value={netPaymentTerms}
              keyName="netPaymentTerms"
            />
          </>
        </DetailRow>
      </div>
    </SectionWrapper>
  )
}
